
import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useForm } from 'react-hook-form';
import { CircleCheck, Phone, MapPin, Building, Mail } from 'lucide-react';
import ScrollReveal from '@/components/ui/ScrollReveal';
import { submitToPabbly } from '@/utils/pabblyConnect';
import { toast } from '@/hooks/use-toast';
import { cities, industries, City, Industry } from '@/data/cityIndustryData';

type FormData = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  businessName: string;
  message?: string;
};

interface CityIndustryLandingProps {
  industrySlug?: string;
  citySlug?: string;
}

const CityIndustryLanding: React.FC<CityIndustryLandingProps> = ({ 
  industrySlug: propIndustrySlug, 
  citySlug: propCitySlug 
}) => {
  const city = cities[propCitySlug || ''] as City | undefined;
  const industry = industries[propIndustrySlug || ''] as Industry | undefined;
  
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<FormData>();

  if (!city || !industry) {
    console.error('City or industry not found:', { citySlug: propCitySlug, industrySlug: propIndustrySlug });
    return (
      <PageLayout 
        title="Page Not Found" 
        description="The requested industry or city page could not be found."
      >
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Page Not Found</h1>
            <p className="mb-8">The industry or city you're looking for doesn't exist in our system.</p>
            <Button asChild>
              <a href="/">Return Home</a>
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const schemaData = [{
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `Web Design for ${industry.name} in ${city.name}, LA`,
    "description": `Professional web design services for ${industry.name.toLowerCase()} in ${city.name}, Louisiana. Custom websites tailored to ${industry.singularName.toLowerCase()} businesses.`,
    "provider": {
      "@type": "Organization",
      "name": "Astrem",
      "image": "https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/evan_profile.jpg",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "123 Main Street",
        "addressLocality": "Baton Rouge",
        "addressRegion": "LA",
        "postalCode": "70801",
        "addressCountry": "US"
      },
      "telephone": "225-331-6114"
    },
    "areaServed": {
      "@type": "City",
      "name": `${city.name}, LA`
    },
    "serviceType": "Web Design"
  }];

  const onSubmit = async (data: FormData) => {
    try {
      await submitToPabbly(
        {
          ...data,
          city: city.name,
          industry: industry.name,
          source: `${industry.name} in ${city.name} Landing Page`
        }, 
        'industry_city_landing_form'
      );
      
      if (window.dataLayer) {
        window.dataLayer.push({
          'event': 'form_submission',
          'form_name': 'industry_city_landing',
          'industry': industry.name,
          'city': city.name,
          'submission_page': window.location.pathname
        });
      }
      
      reset();
      
      toast({
        title: "Message sent!",
        description: "We'll be in touch with you shortly.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "There was a problem sending your message. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <PageLayout 
      title={`Web Design for ${industry.name} in ${city.name}, LA | Astrem`}
      description={`Professional web design services for ${industry.name.toLowerCase()} in ${city.name}, LA. Custom websites tailored to ${industry.singularName.toLowerCase()} businesses with features like ${industry.features.slice(0, 3).join(', ')} and more.`}
      schemaData={schemaData}
    >
      <section className="relative min-h-[90vh] flex items-center bg-gradient-to-br from-zinc-900 to-zinc-800 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-b from-black to-transparent"></div>
          <div className="h-full w-full" 
               style={{ 
                 backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E\")",
               }}
          ></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div className="space-y-6">
                <span className="inline-block px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-400 text-sm font-medium">
                  {industry.name} Web Design
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-medium text-white">
                  Web Design for {industry.name} in {city.name}, LA
                </h1>
                <p className="text-xl text-gray-300 max-w-xl">
                  Custom websites designed specifically for {industry.singularName.toLowerCase()} businesses in {city.name}. Stand out from your local competition with a website that converts visitors into customers.
                </p>
                <Button size="lg" className="bg-blue-500 hover:bg-blue-600 text-white rounded-full">
                  Get a Free Quote
                </Button>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 md:p-8 border border-white/20">
                <h3 className="text-xl font-medium text-white mb-6">Request a Free Quote for Your {industry.singularName}</h3>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Input
                        {...register("firstName", { required: true })}
                        placeholder="First Name"
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-400"
                      />
                    </div>
                    <div>
                      <Input
                        {...register("lastName", { required: true })}
                        placeholder="Last Name"
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-400"
                      />
                    </div>
                  </div>
                  <Input
                    {...register("email", { required: true })}
                    type="email"
                    placeholder="Email Address"
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-400"
                  />
                  <Input
                    {...register("phone", { required: true })}
                    placeholder="Phone Number"
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-400"
                  />
                  <Input
                    {...register("businessName", { required: true })}
                    placeholder={`${industry.singularName} Name`}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-400"
                  />
                  <Textarea
                    {...register("message")}
                    placeholder={`Tell us about your ${industry.singularName.toLowerCase()} website needs (optional)`}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-400"
                  />
                  <Button 
                    type="submit" 
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : `Get Your Free ${industry.singularName} Website Quote`}
                  </Button>
                </form>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <img 
                src={`https://source.unsplash.com/random/800x600/?${city.name.toLowerCase()},louisiana`} 
                alt={`${city.name}, Louisiana`} 
                className="rounded-xl h-full object-cover shadow-lg w-full"
              />
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="space-y-6">
                <span className="inline-block px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                  {city.name}, Louisiana
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium">
                  Web Design Solutions for {industry.name} in {city.name}
                </h2>
                <p className="text-lg text-gray-600">
                  {city.name} is known for its {city.businesses}. {city.highlight}, a strong online presence is essential for {industry.singularName.toLowerCase()} businesses to thrive.
                </p>
                <p className="text-lg text-gray-600">
                  Our web design solutions are tailored specifically for {industry.name.toLowerCase()} in {city.name}, with in-person consultations and ongoing support to help you succeed online. {city.uniqueSellingPoint}.
                </p>
                <div className="flex items-center space-x-2">
                  <MapPin className="h-5 w-5 text-blue-500" />
                  <span>In-person consultations available in {city.name}</span>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium mb-4">
                Industry Solutions
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium">
                Web Design Challenges for {industry.name}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto mt-4">
                {industry.singularName} businesses face unique challenges online. Our tailored solutions address these specific needs to help you succeed.
              </p>
            </div>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industry.challenges.map((challenge, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
                  <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-blue-500 mb-6">
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-medium mb-3">{challenge}</h3>
                  <p className="text-gray-600">{industry.solutions[index]}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium mb-4">
                Website Features
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium">
                Essential Features for {industry.name} Websites
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto mt-4">
                Our websites for {industry.singularName.toLowerCase()} businesses in {city.name} include these key features to help you attract and convert customers.
              </p>
            </div>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {industry.features.map((feature, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="p-6 rounded-xl border border-gray-200 hover:border-blue-500/50 transition-colors">
                  <CircleCheck className="w-8 h-8 text-blue-500 mb-4" />
                  <h3 className="text-lg font-medium mb-2">{feature}</h3>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
            <div className="lg:col-span-3">
              <ScrollReveal>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Why Choose a Local Web Designer for Your {city.name} {industry.singularName}?
                </h2>
                <p className="text-xl opacity-90 mb-8">
                  We understand the {city.name} market and the unique needs of {industry.name.toLowerCase()} in the area.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex items-start">
                    <div className="mr-4 p-1 bg-white/10 rounded-full">
                      <MapPin className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-medium text-lg">Local Understanding</h3>
                      <p className="opacity-80">We know the {city.name} market and customer expectations</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="mr-4 p-1 bg-white/10 rounded-full">
                      <Building className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-medium text-lg">Industry Expertise</h3>
                      <p className="opacity-80">Specialized in {industry.name.toLowerCase()} websites</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="mr-4 p-1 bg-white/10 rounded-full">
                      <Phone className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-medium text-lg">In-Person Support</h3>
                      <p className="opacity-80">Face-to-face consultations in {city.name}</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="mr-4 p-1 bg-white/10 rounded-full">
                      <Mail className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-medium text-lg">Ongoing Support</h3>
                      <p className="opacity-80">Local assistance whenever you need it</p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
            
            <div className="lg:col-span-2">
              <ScrollReveal delay={200}>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                  <h3 className="text-2xl font-medium mb-6">Get Your Free Consultation</h3>
                  <p className="mb-6 opacity-90">
                    We'll discuss your {industry.singularName.toLowerCase()} website needs and provide a no-obligation quote.
                  </p>
                  <Button asChild size="lg" className="w-full bg-white text-blue-600 hover:bg-gray-100">
                    <a href="#quote-form">Request a Free Quote</a>
                  </Button>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      <section id="quote-form" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div className="space-y-6">
                <span className="inline-block px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                  Get Started
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium">
                  Ready to Transform Your {industry.singularName}'s Online Presence?
                </h2>
                <p className="text-lg text-gray-600">
                  Fill out the form to get a free quote for your {industry.singularName.toLowerCase()} website in {city.name}. We'll contact you to discuss your specific needs and how we can help you attract more customers.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <CircleCheck className="w-5 h-5 text-blue-500 mr-3 mt-1" />
                    <p>Free, no-obligation consultation for {city.name} businesses</p>
                  </div>
                  <div className="flex items-start">
                    <CircleCheck className="w-5 h-5 text-blue-500 mr-3 mt-1" />
                    <p>Industry-specific solutions for {industry.name.toLowerCase()}</p>
                  </div>
                  <div className="flex items-start">
                    <CircleCheck className="w-5 h-5 text-blue-500 mr-3 mt-1" />
                    <p>Local, in-person support available in {city.name}</p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
                <h3 className="text-xl font-medium mb-6">Get Your Free Quote</h3>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Input
                        {...register("firstName", { required: true })}
                        placeholder="First Name"
                      />
                    </div>
                    <div>
                      <Input
                        {...register("lastName", { required: true })}
                        placeholder="Last Name"
                      />
                    </div>
                  </div>
                  <Input
                    {...register("email", { required: true })}
                    type="email"
                    placeholder="Email Address"
                  />
                  <Input
                    {...register("phone", { required: true })}
                    placeholder="Phone Number"
                  />
                  <Input
                    {...register("businessName", { required: true })}
                    placeholder={`${industry.singularName} Name`}
                  />
                  <Textarea
                    {...register("message")}
                    placeholder={`Tell us about your ${industry.singularName.toLowerCase()} website needs (optional)`}
                  />
                  <Button 
                    type="submit" 
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : `Get a Free ${industry.singularName} Website Quote`}
                  </Button>
                  <p className="text-sm text-center text-gray-500 mt-4">
                    By submitting this form, you're taking the first step toward growing your {industry.singularName.toLowerCase()} business online in {city.name}.
                  </p>
                </form>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default CityIndustryLanding;
